import { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useParams, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { setBrandCache } from "@/lib/card";
import { themeVars } from "@/lib/siteThemes";
import { SiteNav, Hero, Rates, About, Contact, SiteFooter, WhatsAppFab } from "@/components/site/SiteSections";
import { Products, CertCheck, Enquiry } from "@/components/site/SiteInteractive";
import SiteCertificate from "@/components/site/SiteCertificate";

export function SiteShell({ payload, base }) {
  const { site, brand, products } = payload;
  const { theme, style } = themeVars(site);
  const [product, setProduct] = useState("");
  useEffect(() => {
    setBrandCache(brand);
    document.title = brand.store_name || site.hero_title || "Jewellery showroom";
  }, [brand, site]);
  const ctx = { site, brand, theme, base };

  return (
    <div style={style} className="min-h-screen" data-testid="store-site" data-theme={site.theme}>
      <SiteNav {...ctx} />
      <Routes>
        <Route path="certificate/:rid" element={<SiteCertificate {...ctx} />} />
        <Route path="*" element={
          <main>
            <Hero {...ctx} />
            {site.show_rates && <Rates {...ctx} />}
            <Products {...ctx} products={products} onEnquire={(p) => { setProduct(p); document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" }); }} />
            <CertCheck {...ctx} />
            <About {...ctx} />
            <Enquiry {...ctx} product={product} setProduct={setProduct} />
            <Contact {...ctx} />
          </main>
        } />
      </Routes>
      <SiteFooter {...ctx} />
      <WhatsAppFab {...ctx} />
    </div>
  );
}

function SiteLoader({ request, base }) {
  const [payload, setPayload] = useState(null);
  const certMatch = useLocation().pathname.match(/\/certificate\/([^/]+)/);
  useEffect(() => { request().then((r) => setPayload(r.data)).catch(() => setPayload(false)); }, [request]);
  if (payload === null) return <div className="min-h-screen grid place-items-center bg-stone-950 text-amber-200"><Loader2 className="animate-spin" /></div>;
  if (!payload && certMatch) {
    if (base) return <Navigate to={`/verify/${certMatch[1]}`} replace />;
    window.location.replace(`${BACKEND_URL}/verify/${certMatch[1]}`);
    return null;
  }
  if (!payload)
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-700 p-6 text-center" data-testid="store-site-not-found">
        <div>
          <p className="text-2xl font-bold" style={{ fontFamily: "Cinzel, serif" }}>Showroom not found</p>
          <p className="text-sm mt-2">This jewellery website is not available right now.</p>
          <Link to="/verify" className="inline-block mt-4 text-sm font-semibold text-amber-800 hover:underline">Verify a certificate →</Link>
        </div>
      </div>
    );
  return <SiteShell payload={payload} base={base} />;
}

export default function StoreSite() {
  const { slug } = useParams();
  const [request] = useState(() => () => api.get(`/public/site/${slug}`));
  return <SiteLoader request={request} base={`/store/${slug}`} />;
}

export function DomainSite() {
  const [request] = useState(() => () => api.get("/public/site-by-domain", { params: { host: window.location.hostname } }));
  return <SiteLoader request={request} base="" />;
}
